import {Helmet} from '@dr.pogodin/react-helmet';
import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {Button, Separator} from 'platform/components';
import {Hide, HStack, Show, VStack} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';

import {lazy, Suspense, useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';

import {head, isEmpty, isNil, isNotNil} from 'ramda';

import {
  useGetSourcingAuctionCountQuery,
  useSourcingGetVehicleCountQuery,
  useSourcingVehicleDetailQuery,
  useSourcingVehicleLocationsQuery,
} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {sourcingRoutes, testIds} from '@dms/routes';
import {formatDocumentTitle} from '@dms/shared';

import {composePath, useBoolean, useDebouncedValue, useNavigate} from 'shared/web';

import {AuctionPreview} from '../../components/AuctionPreview/AuctionPreview';
import {SourcingDataStatus} from '../../components/SourcingDataStatus/SourcingDataStatus';
import {SourcingLayout} from '../../components/SourcingLayout/SourcingLayout';
import {useSourcingTable} from '../../components/SourcingTable/hooks/useSourcingTable';
import {useSourcingTableColumns} from '../../components/SourcingTable/hooks/useSourcingTableColumns';
import {SourcingTable} from '../../components/SourcingTable/SourcingTable';
import {
  SourcingViewMode,
  sourcingTableSessionStorage,
} from '../../components/SourcingTable/utils/sourcingTableSessionStorage';
import {VehiclePreview} from '../../components/VehiclePreview/VehiclePreview';
import {AUCTION_DEFAULT_ORDER_ID} from '../../constants/AUCTION_DEFAULT_ORDER_ID';
import {AUCTION_ORDER_BY_MAP, AUCTION_SORT_OPTIONS} from '../../constants/auctionSortOptions';
import {CLASSIFIEDS_DEFAULT_ORDER_ID} from '../../constants/CLASSIFIEDS_DEFAULT_ORDER_ID';
import {
  CLASSIFIEDS_ALL_OFFER_ORDER_BY_MAP,
  CLASSIFIEDS_ALL_OFFER_SORT_OPTIONS,
} from '../../constants/classifiedsSortBy';
import {REAL_TIME_POLLING_INTERVAL} from '../../constants/REAL_TIME_POLLING_INTERVAL';
import {rememberSourcingPrevUrl} from '../../constants/rememberSourcingPrevUrl';
import {useAuctionEnumLookup} from '../../hooks/useAuctionEnumLookup';
import {useSortOrderByFromUrl} from '../../hooks/useSortOrderByFromUrl';
import {useSourcingCurrency} from '../../hooks/useSourcingCurrency';
import {useSourcingListAvailability} from '../../hooks/useSourcingListAvailability';
import {useSourcingPricingStrategy} from '../../hooks/useSourcingPricingStrategy';
import {useSourcingTargetCountry} from '../../hooks/useSourcingTargetCountry';
import {SourcingType, useSourcingType} from '../../hooks/useSourcingType';
import {SourcingTableQueryResult} from '../../types/SourcingTableQueryResult';
import {adaptAuctionVehicleToSourcingItem} from '../../utils/adaptAuctionVehicleToSourcingItem';
import {getAuctionTableColumns} from '../../utils/getAuctionTableColumns';
import {getSourcingVehicleId} from '../../utils/getSourcingVehicleId';
import {isAuctionVehicle} from '../../utils/isAuctionVehicle';
import {isUnifiedSourcingEnabled} from '../../utils/isUnifiedSourcingEnabled';
import {ClassifiedsMapSkeleton} from './components/ClassifiedsMap/ClassifiedsMapSkeleton';
import {MapPreviewPlaceholder} from './components/ClassifiedsMap/MapPreviewPlaceholder';
import {FilterPresetButtons} from './components/FilterPresetButtons/FilterPresetButtons';
import {SavedFiltersMobileBar} from './components/FilterPresetButtons/SavedFiltersMobileBar';
import {Filters} from './components/Filters/Filters';
import {useFilters} from './components/FiltersContext/FiltersContext';
import {FilterTags} from './components/FilterTags/FilterTags';
import {useAuctionsVehicleListQueries} from './hooks/useAuctionsVehicleListQueries';
import {useClassifiedsVehicleListQueries} from './hooks/useClassifiedsVehicleListQueries';
import {useSourcingFilterPresets} from './hooks/useSourcingFilterPresets';
import {createAuctionFilterBody} from './utils/createAuctionFilterBody';
import {createFilterBody} from './utils/createFilterBody';
import {getApplicableFilters} from './utils/getApplicableFilters';
import {hasSettledAllOffersListResponse} from './utils/hasSettledAllOffersListResponse';
import {hasSomeFilters} from './utils/hasSomeFilters';
import {omitHiddenOriginCountryFilter} from './utils/omitHiddenOriginCountryFilter';
import {omitHiddenVanFilters} from './utils/omitHiddenVanFilters';

const ClassifiedsMap = lazy(() =>
  import('./components/ClassifiedsMap/ClassifiedsMap').then((module) => ({
    default: module.ClassifiedsMap,
  }))
);

// eslint-disable-next-line complexity, max-lines-per-function, max-statements -- Pre-existing violation
export function AllOffersPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const sourcingType = useSourcingType();
  const [selectedViewMode, setSelectedViewMode] = useState<SourcingViewMode>(() =>
    sourcingTableSessionStorage.getAllOffersViewMode()
  );
  const isClassifiedsMapEnabled = isFeatureEnabled(featureFlags.SOURCING_CLASSIFIEDS_MAP);
  const isMapView =
    sourcingType === SourcingType.CLASSIFIEDS &&
    isClassifiedsMapEnabled &&
    selectedViewMode === 'map';
  const visibleViewMode = selectedViewMode === 'map' && !isMapView ? 'table' : selectedViewMode;
  const [selectedMapAdId, setSelectedMapAdId] = useState<string | null | undefined>(() => {
    const savedMapAdId = sourcingTableSessionStorage.getMapSelectedVehicleId();
    return savedMapAdId === undefined
      ? (sourcingTableSessionStorage.getLastVisitedVehicleId('all-vehicles') ?? undefined)
      : savedMapAdId;
  });
  const enumLookup = useAuctionEnumLookup();
  const formatNumber = useFormatNumber();
  const [isFiltersOpen, openFiltersView, closeFiltersView] = useBoolean();
  const filterPresetsControl = useSourcingFilterPresets(isFiltersOpen);
  const [classifiedsState, classifiedsDispatch] = useSourcingTable(
    'all-vehicles',
    CLASSIFIEDS_DEFAULT_ORDER_ID,
    filterPresetsControl.appliedFilterPresetId
  );
  const [auctionsState, auctionsDispatch] = useSourcingTable(
    'auctions',
    AUCTION_DEFAULT_ORDER_ID,
    filterPresetsControl.appliedFilterPresetId
  );
  const [state, dispatch] =
    sourcingType === SourcingType.CLASSIFIEDS
      ? [classifiedsState, classifiedsDispatch]
      : [auctionsState, auctionsDispatch];
  const selectedAdId = state.selectedRow?.sourcingVehicle?.adId;
  // Keep a selected list row as the initial map preview until the user picks or clears a map ad.
  const savedMapPreviewAdId = selectedMapAdId === undefined ? selectedAdId : selectedMapAdId;
  const onChangeViewMode = (mode: SourcingViewMode) => {
    if (mode === 'map' && !isMapView) {
      // The map must open on the car selected in the list, even if it held an older selection.
      setSelectedMapAdId(selectedAdId ?? null);
      sourcingTableSessionStorage.setMapSelectedVehicleId(selectedAdId ?? null);
    } else if (isMapView && mode !== 'map' && savedMapPreviewAdId !== selectedAdId) {
      // Let the list restore the map selection once its rows have loaded again.
      classifiedsDispatch({type: 'selectRow', row: null});
    }
    setSelectedViewMode(mode);
    sourcingTableSessionStorage.setAllOffersViewMode(mode);
  };
  const selectMapAd = (adId: string) => {
    setSelectedMapAdId(adId);
    sourcingTableSessionStorage.setMapSelectedVehicleId(adId);
    sourcingTableSessionStorage.setLastVisitedVehicleId(state.tableName, adId);
  };
  const onOpenDetail = (adId: string) => {
    selectMapAd(adId);
    rememberSourcingPrevUrl(location);
    navigate(
      composePath(
        sourcingType === SourcingType.AUCTIONS
          ? sourcingRoutes.auctionDetail
          : sourcingRoutes.vehicleDetail,
        {params: {adId}}
      )
    );
  };
  // The stored toggle outlives the flag, so a hidden switch must not keep polling.
  const isLivePollingEnabled =
    state.isRealTime && isFeatureEnabled(featureFlags.SOURCING_REAL_TIME_ADVERTISEMENT);
  const sourcingCurrency = useSourcingCurrency();
  const sourcingTargetCountry = useSourcingTargetCountry();
  const pricingStrategy = useSourcingPricingStrategy();
  const {filters} = useFilters();
  const debouncedFilters = useDebouncedValue(filters, 500);
  const filterBody = isFiltersOpen ? debouncedFilters : filters;
  const isVanFiltersEnabled = isFeatureEnabled(featureFlags.SOURCING_VAN_FILTERS);
  const isOriginCountryFilterEnabled = isFeatureEnabled(
    featureFlags.SOURCING_ORIGIN_COUNTRY_FILTER
  );

  const classifiedsColumns = useSourcingTableColumns();
  const auctionColumns = getAuctionTableColumns({formatNumber, enumLookup});
  const columns = sourcingType === SourcingType.CLASSIFIEDS ? classifiedsColumns : auctionColumns;

  const sortByOptions =
    sourcingType === SourcingType.AUCTIONS
      ? AUCTION_SORT_OPTIONS
      : CLASSIFIEDS_ALL_OFFER_SORT_OPTIONS;
  const {orderBy: sortOrderByFromUrl, clear: clearUrlSort} = useSortOrderByFromUrl();
  const orderBy =
    sortOrderByFromUrl ??
    (sourcingType === SourcingType.AUCTIONS
      ? AUCTION_ORDER_BY_MAP.get(state.orderBy)
      : CLASSIFIEDS_ALL_OFFER_ORDER_BY_MAP.get(state.orderBy));
  const currency = sourcingCurrency?.code;
  const country = sourcingTargetCountry?.code;

  // useMemo for correctness: RTK Query cache-key stability.
  const classifiedsFilterBody = useMemo(() => {
    if (isNil(country)) {
      return null;
    }
    const presetFilters = filterPresetsControl.selectedFilterPreset?.filters;
    const vanFilteredInput = omitHiddenVanFilters({
      filters: filterBody,
      filterPresetId: filterPresetsControl.appliedFilterPresetId,
      presetFilters,
      isVanFiltersEnabled,
    });
    const classifiedsRequestInput = omitHiddenOriginCountryFilter({
      ...vanFilteredInput,
      presetFilters,
      isOriginCountryFilterEnabled,
    });
    return createFilterBody(
      classifiedsRequestInput.filters,
      classifiedsRequestInput.filterPresetId,
      country
    );
  }, [
    filterBody,
    filterPresetsControl.appliedFilterPresetId,
    filterPresetsControl.selectedFilterPreset?.filters,
    country,
    isVanFiltersEnabled,
    isOriginCountryFilterEnabled,
  ]);
  // useMemo for correctness: RTK Query cache-key stability.
  // Auctions never send the category: `all_auctions` silently ignores `vehicle_type`, so a
  // category there would look applied while returning every vehicle. Van body styles do filter.
  const auctionsFilterBody = useMemo(
    () =>
      isNotNil(country)
        ? createAuctionFilterBody(filterBody, filterPresetsControl.appliedFilterPresetId, country)
        : null,
    [filterBody, filterPresetsControl.appliedFilterPresetId, country]
  );

  const classifiedsQueryResult = useClassifiedsVehicleListQueries({
    filterBody: classifiedsFilterBody,
    orderBy,
    currency,
    pricingStrategy,
    page: state.page,
    isFiltersOpen,
    isRealTime: isLivePollingEnabled,
    pollingInterval: REAL_TIME_POLLING_INTERVAL,
    skip: sourcingType === SourcingType.AUCTIONS || isMapView,
  });
  const auctionsQueryResult = useAuctionsVehicleListQueries({
    filterBody: auctionsFilterBody,
    orderBy,
    currency,
    buyerCountry: country,
    pricingStrategy,
    isFiltersOpen,
    isRealTime: isLivePollingEnabled,
    page: state.page,
    pollingInterval: REAL_TIME_POLLING_INTERVAL,
    skip: sourcingType === SourcingType.CLASSIFIEDS,
  });

  const listQueryResult =
    sourcingType === SourcingType.CLASSIFIEDS ? classifiedsQueryResult : auctionsQueryResult;
  const data =
    sourcingType === SourcingType.CLASSIFIEDS
      ? classifiedsQueryResult.currentData?.data
      : auctionsQueryResult.currentData?.data.map((item) =>
          adaptAuctionVehicleToSourcingItem(item, enumLookup)
        );

  const {data: classifiedsCount} = useSourcingGetVehicleCountQuery(
    isNotNil(classifiedsFilterBody)
      ? {body: classifiedsFilterBody, currency, pricingStrategy}
      : skipToken,
    {
      pollingInterval: isLivePollingEnabled ? REAL_TIME_POLLING_INTERVAL : undefined,
      skipPollingIfUnfocused: true,
      refetchOnFocus: isLivePollingEnabled,
      skip: sourcingType === SourcingType.AUCTIONS,
    }
  );
  // Not polled: auction real-time only refreshes loaded rows, it never adds new ones.
  const {data: auctionsCount} = useGetSourcingAuctionCountQuery(
    isNotNil(auctionsFilterBody)
      ? {body: auctionsFilterBody, currency, pricingStrategy}
      : skipToken,
    {skip: sourcingType === SourcingType.CLASSIFIEDS}
  );
  const count = (sourcingType === SourcingType.CLASSIFIEDS ? classifiedsCount : auctionsCount)
    ?.count;
  const {data: mapLocations} = useSourcingVehicleLocationsQuery(
    isMapView && !isFiltersOpen && isNotNil(classifiedsFilterBody)
      ? {body: classifiedsFilterBody, currency, pricingStrategy}
      : skipToken
  );
  const mapCandidateAdId =
    isNotNil(savedMapPreviewAdId) &&
    (mapLocations?.items.some(({adId}) => adId === savedMapPreviewAdId) ||
      mapLocations?.missingLocationAdIds?.includes(savedMapPreviewAdId))
      ? savedMapPreviewAdId
      : null;
  const {error: mapCandidateError} = useSourcingVehicleDetailQuery(
    isNotNil(mapCandidateAdId)
      ? {
          externalId: mapCandidateAdId,
          currency,
          buyerCountry: country,
          pricingStrategy,
        }
      : skipToken
  );
  const mapPreviewAdId =
    mapCandidateError && 'status' in mapCandidateError && mapCandidateError.status === 404
      ? null
      : mapCandidateAdId;
  const onSelectMapAd = (adId: string) => {
    if (mapPreviewAdId === adId) {
      setSelectedMapAdId(null);
      sourcingTableSessionStorage.setMapSelectedVehicleId(null);
    } else {
      selectMapAd(adId);
    }
  };

  const activeFilterBody =
    sourcingType === SourcingType.CLASSIFIEDS ? classifiedsFilterBody : auctionsFilterBody;
  const isListQueryEnabled = isNotNil(activeFilterBody) && !isMapView;
  const hasSettledListResponse = hasSettledAllOffersListResponse({
    data,
    isEndReached: listQueryResult.isEndReached,
    isLoading: listQueryResult.isLoading,
    isFetching: listQueryResult.isFetching,
    isListQueryEnabled,
  });
  const isFetchingFreshData =
    filterPresetsControl.isLoading || (isListQueryEnabled && !hasSettledListResponse);
  // Filters that do not apply here have no chip, so they must not highlight the filter button.
  const hasFilters = hasSomeFilters(
    getApplicableFilters(filters, sourcingType, {isVanFiltersEnabled, isOriginCountryFilterEnabled})
  );
  const tableQueryResult: SourcingTableQueryResult = {
    data: data ?? [],
    count,
    isLoading: isFetchingFreshData,
    isFetching: listQueryResult.isFetching,
    error: listQueryResult.error,
    onRetry: listQueryResult.reset,
    lastUpdateTime: listQueryResult.fulfilledTimeStamp,
    isPolling: listQueryResult.isPolling,
    scrollAttemptedOffset: listQueryResult.scrollAttemptedOffset,
    isEndReached: listQueryResult.isEndReached,
  };
  const hasCompletedWithNoVisibleResults =
    !isMapView &&
    !filterPresetsControl.isLoading &&
    !listQueryResult.isError &&
    isEmpty(data ?? []) &&
    hasSettledListResponse &&
    count === 0;

  // Look up the selected row by ID because the saved row can have stale pricing after a currency switch.
  const preview =
    data?.find((row) => row.sourcingVehicle.adId === selectedAdId) ?? head(data ?? []);

  const listQueryKey = JSON.stringify({
    filterBody: activeFilterBody,
    orderBy: state.orderBy,
    currency,
    country,
    pricingStrategy,
  });

  const classifiedsAdIds =
    sourcingType === SourcingType.CLASSIFIEDS
      ? (data ?? []).map((row) => row.sourcingVehicle.adId)
      : [];
  const availabilityUserLoadKey = `${listQueryKey}|${listQueryResult.scrollAttemptedOffset ?? 0}`;
  useSourcingListAvailability({
    adIds: classifiedsAdIds,
    enabled:
      isFeatureEnabled(featureFlags.SOURCING_AVAILABILITY_CHECK) &&
      sourcingType === SourcingType.CLASSIFIEDS &&
      !isFiltersOpen,
    userLoadKey: availabilityUserLoadKey,
  });

  const pageTitle = formatDocumentTitle(
    sourcingType === SourcingType.CLASSIFIEDS
      ? i18n.t('page.sourcing.labels.classifieds')
      : i18n.t('page.sourcing.labels.auctions'),
    i18n.t('page.sourcing.labels.sourcing')
  );

  if (isFiltersOpen) {
    return (
      <>
        <Helmet title={pageTitle} />
        <Filters
          filterPresetsControl={filterPresetsControl}
          count={count ?? 0}
          onClose={closeFiltersView}
          data-testid={testIds.sourcing.classifieds('filters')}
        />
      </>
    );
  }

  const filtersButton = (
    <Button
      variant={hasFilters ? 'primary' : 'outlined'}
      title={i18n.t('page.filters.labels.filters')}
      leftIcon="content/filter_list"
      onClick={openFiltersView}
      data-testid={testIds.sourcing.classifieds('filters')}
    />
  );

  return (
    <SourcingLayout
      hasPreview={isMapView ? isNotNil(mapPreviewAdId) : isNotNil(preview) || isFetchingFreshData}
      isPreviewLoading={!isMapView && isFetchingFreshData && isNil(preview)}
      pageTitle={pageTitle}
      header={
        <VStack width="100%" spacing={4}>
          <Hide onMobile onTablet>
            <HStack spacing={4}>
              {filtersButton}
              <Show when={!filterPresetsControl.isEmpty}>
                <Separator orientation="vertical" spacing={0} />
              </Show>
              <FilterPresetButtons filterPresetsControl={filterPresetsControl} isCollapsible />
            </HStack>
          </Hide>

          <Show onMobile onTablet>
            <HStack spacing={2} align="center" wrap justify="space-between">
              {filtersButton}
              <SavedFiltersMobileBar filterPresetsControl={filterPresetsControl} />
            </HStack>
          </Show>

          <Show when={hasFilters}>
            <FilterTags
              filterPresetsControl={filterPresetsControl}
              data-testid={testIds.sourcing.classifieds('filterHeaderChips')}
            />
          </Show>
        </VStack>
      }
      content={
        <SourcingDataStatus
          isEmpty={hasCompletedWithNoVisibleResults}
          iconUrl="/assets/images/empty_images/empty-sourcing.svg"
          text={i18n.t('general.notifications.noResults')}
          textSecondLine={i18n.t('page.filters.notifications.removeSearchFilters')}
          noResultsContent={
            <Button
              title={i18n.t('page.filters.labels.filters')}
              leftIcon="content/filter_list"
              onClick={openFiltersView}
              data-testid="button-classifieds-allOffers-empty.filter"
            />
          }
        >
          <SourcingTable
            state={state}
            dispatch={dispatch}
            columns={columns}
            queryResult={tableQueryResult}
            listQueryKey={listQueryKey}
            sortByOptions={sortByOptions}
            isRealTimeEnabled
            realTimeOrderBy={
              sourcingType === SourcingType.CLASSIFIEDS ? CLASSIFIEDS_DEFAULT_ORDER_ID : undefined
            }
            isSortByUnset={isNotNil(sortOrderByFromUrl)}
            onClearUrlSort={clearUrlSort}
            viewMode={visibleViewMode}
            onViewModeChange={onChangeViewMode}
            mapView={
              sourcingType === SourcingType.CLASSIFIEDS && isClassifiedsMapEnabled ? (
                <Suspense
                  fallback={
                    isUnifiedSourcingEnabled() ? (
                      <ClassifiedsMapSkeleton />
                    ) : (
                      i18n.t('page.sourcing.map.loading')
                    )
                  }
                >
                  <ClassifiedsMap
                    filterBody={classifiedsFilterBody}
                    selectedAdId={mapPreviewAdId}
                    onSelectAd={onSelectMapAd}
                    onOpenAdDetail={onOpenDetail}
                    onOpenFilters={openFiltersView}
                    isRealTime={isLivePollingEnabled}
                  />
                </Suspense>
              ) : undefined
            }
          />
        </SourcingDataStatus>
      }
      iframe={(() => {
        if (hasCompletedWithNoVisibleResults) {
          return null;
        }

        if (isMapView) {
          return isNotNil(mapPreviewAdId) ? (
            <VehiclePreview key={mapPreviewAdId} vehicleId={mapPreviewAdId} />
          ) : (
            <MapPreviewPlaceholder />
          );
        }

        return sourcingType === SourcingType.AUCTIONS && isAuctionVehicle(preview) ? (
          <AuctionPreview
            key={preview.auctionVehicle.adId}
            auction={preview}
            data-testid={testIds.sourcing.auctions('preview')}
          />
        ) : (
          <VehiclePreview
            key={getSourcingVehicleId(preview)}
            vehicleId={getSourcingVehicleId(preview)}
          />
        );
      })()}
    />
  );
}
